import type { ReactNode } from 'react';

// Occurrences over a range, as thin bars: shape, not numbers (those sit beside it). Bars, not a line, because a bucket
// with nothing in it is a fact worth seeing.
export function Sparkline(props: { values: readonly number[]; width?: number; height?: number; tone?: 'critical' | 'data' | 'muted' }): ReactNode {
    const width = props.width ?? 120;
    const height = props.height ?? 22;
    const max = Math.max(1, ...props.values);
    const step = width / Math.max(1, props.values.length);
    const bar = Math.max(1, step - (step > 3 ? 1 : 0));

    return (
        <svg className={`spark ${props.tone ?? 'critical'}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
            <line x1="0" x2={width} y1={height - 0.5} y2={height - 0.5} />
            {props.values.map((value, index) =>
                value > 0 ? (
                    <rect
                        key={index}
                        x={index * step}
                        y={height - Math.max(2, (value / max) * height)}
                        width={bar}
                        height={Math.max(2, (value / max) * height)}
                    />
                ) : null,
            )}
        </svg>
    );
}

// A trend, as a thin line with a wash under it and a dot on its last value: the shape of a number beside it.
export function Trend(props: { values: readonly number[]; color: string; width?: number; height?: number }): ReactNode {
    const width = props.width ?? 132;
    const height = props.height ?? 28;
    const count = props.values.length;
    if (count < 2) {
        return null;
    }
    const max = Math.max(1, ...props.values);
    const x = (index: number): number => 1 + (index / (count - 1)) * (width - 6);
    const y = (value: number): number => 3 + (1 - value / max) * (height - 7);
    const line = props.values.map((value, index) => `${index ? 'L' : 'M'}${x(index).toFixed(1)},${y(value).toFixed(1)}`).join('');
    const lastX = x(count - 1);
    const lastY = y(props.values[count - 1] ?? 0);

    return (
        <svg className="trend" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
            <path d={`${line}L${lastX},${height}L1,${height}Z`} fill={props.color} opacity={0.1} />
            <path d={line} fill="none" stroke={props.color} strokeWidth={1.7} strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={lastX} cy={lastY} r={3.2} fill={props.color} stroke="var(--s1)" strokeWidth={2} />
        </svg>
    );
}
